import { twMerge } from "tailwind-merge";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import {
  AdvancedMarker,
  APIProvider,
  InfoWindow,
  Map,
  Marker,
  useMap,
} from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { CUSTOMER, ORDER_BUYER } from "../../constants/routes";
import { useBasketContext, useEvents } from "../../hooks";
import { useSupplier } from "../../hooks/api/v2/supplier/useSupplier";
import { useSupplierStorefrontV2 } from "../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import { useMarketStorefrontSessionId } from "../../hooks/useMarketStorefrontSessionTracking";
import { useNavigateToChild } from "../../hooks/useParentNavigation";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { StorefrontDeliveryOffersCard } from "../../pages/stockList/categoryPage/Storefront/StorefrontCards/StorefrontDeliveryOffersCard/StorefrontDeliveryOffersCard";
import { StorefrontOrderRulesCard } from "../../pages/stockList/categoryPage/Storefront/StorefrontCards/StorefrontOrderRulesCard";
import { useStockListOrderSummaryProps } from "../../pages/stockList/useStockListOrderSummaryProps";
import { getSellarWebsiteUtms } from "../../utils/utmHelper";
import { Badge, BadgePurple } from "../badge/Badge";
import { OrderDiscountSummary } from "../discountSummary/OrderDiscountSummary";
import { SelectedFulfillmentTypeCard } from "../fulfillmentTypeCards/SelectedFulfillmentTypeCard";
import { Button, Card, Icon } from "@sellar-io/components";
import { ProfilePicture } from "../profilePicture/ProfilePicture";
import {
  ProfilePictureCollection,
  ProfilePictureCollectionImage,
} from "../profilePictureCollection/profilePictureCollection";
import { SpinnerLegacy } from "..";
import { useSellarMapStore } from "./SellarMapStore";
import { SelectedFulfillmentTypeCardLegacy } from "../fulfillmentTypeCards/SelectedFulfillmentTypeCardLegacy";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { getFulfillmentType, defaultLondonLocation } from "./SellarMap.shared";

export type MarkerEntityType = {
  id: number;
  urlId?: string;
  SupplierRetailerConnectionId?: number;
  Business: {
    name: string;
    logoUrl: string | null;
    Location?: { lat: number; lng: number } | null;
  };
};

export const SellarMap = ({
  entities,
  displaying,
  noClustering,
}: {
  entities?: MarkerEntityType[];
  displaying: "buyer" | "supplier";
  noClustering?: boolean;
}) => {
  const { business } = useSellarCurrentBusiness();
  const { add, location } = useSellarMapStore(
    useShallow((state) => ({
      location: state.location,
      add: state.add,
    })),
  );

  return (
    <ErrorBoundary FallbackComponent={FallbackComponentForSellarMap}>
      <APIProvider
        apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
        libraries={["marker"]}
      >
        <Map
          reuseMaps
          gestureHandling={"greedy"}
          disableDefaultUI
          defaultZoom={10}
          defaultCenter={location ?? defaultLondonLocation}
          defaultHeading={0}
          defaultTilt={0}
          mapId="28065e002b863c4f"
          onCenterChanged={(ev) =>
            add({
              lat: ev.map.getCenter()?.lat() ?? defaultLondonLocation.lat,
              lng: ev.map.getCenter()?.lng() ?? defaultLondonLocation.lng,
            })
          }
        >
          <Marker position={business?.Location} />
          {entities &&
            (noClustering ? (
              <SimpleEntityMarkers entities={entities} />
            ) : (
              <EntityMarkers entities={entities} displaying={displaying} />
            ))}
        </Map>
      </APIProvider>
    </ErrorBoundary>
  );
};

const FallbackComponentForSellarMap = ({ error }: FallbackProps) => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  console.error("SellarMap error:", errorMessage);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1">
      <p className="text-xl">Sorry! Something went wrong.</p>
      <p className="text-sm text-zinc-500">Maps are currently unavailable</p>
    </div>
  );
};

const EntityMarkers = ({
  entities,
  displaying,
}: {
  entities: MarkerEntityType[];
  displaying: "buyer" | "supplier";
}) => {
  const map = useMap();
  const [markers, setMarkers] = useState<{
    [key: string]: google.maps.marker.AdvancedMarkerElement;
  }>({});
  const navigateToChild = useNavigateToChild();
  const clusterer = useRef<MarkerClusterer | null>(null);

  const { activeId, setActiveId } = useSellarMapStore(
    useShallow((state) => ({
      activeId: state.activeId,
      setActiveId: state.setActiveId,
    })),
  );

  useEffect(() => {
    if (!map) return;
    const mapClusterer = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 170 }),
    });
    clusterer.current = mapClusterer;

    return () => {
      // Cached maps outlive this component, so detach its overlays and listeners.
      mapClusterer.setMap(null);
      mapClusterer.clearMarkers(true);
      clusterer.current = null;
    };
  }, [map]);

  // Update markers, if the markers array has changed
  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(Object.values(markers));
  }, [map, markers]);

  const setMarkerRef = (
    marker: google.maps.marker.AdvancedMarkerElement | null,
    key: string,
  ) => {
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      if (marker) {
        return { ...prev, [key]: marker };
      } else {
        const newMarkers = { ...prev };
        delete newMarkers[key];
        return newMarkers;
      }
    });
  };

  return (
    <>
      {entities
        .filter((entity) => entity.Business.Location)
        .map((entity) => (
          <AdvancedMarker
            key={entity.id}
            position={entity.Business.Location}
            ref={(marker) => setMarkerRef(marker, `entity-${entity.id}`)}
            // Small nuance to allow clicking a marker but also allow controlling logic in the child component. Allows click propogation down to the child component
            onClick={() => {
              if (displaying === "buyer") {
                if (entity.SupplierRetailerConnectionId)
                  navigateToChild(
                    CUSTOMER.href({
                      connectionId: entity.SupplierRetailerConnectionId,
                    }),
                  );
              } else {
                setActiveId(entity.id);
              }
            }}
          >
            <PopupMarker
              active={activeId === entity.id}
              entity={entity}
              displaying={displaying}
            />
          </AdvancedMarker>
        ))}
    </>
  );
};

const SimpleEntityMarkers = ({
  entities,
}: {
  entities: MarkerEntityType[];
}) => {
  return (
    <>
      {entities
        .filter((entity) => entity.Business.Location)
        .map((entity) => (
          <AdvancedMarker key={entity.id} position={entity.Business.Location}>
            <div className="relative flex items-center gap-2 rounded-full border border-zinc-300 bg-white p-2 pr-3 text-sm font-medium text-nowrap text-zinc-800 shadow-md">
              <ProfilePicture
                logoUrl={entity.Business.logoUrl}
                name={entity.Business.name}
                size={"small"}
              />
              {entity.Business.name}
              <span className="absolute bottom-[-7px] left-[50%] ml-[-5px] h-3 w-3 rotate-45 border-r border-b border-zinc-300 bg-white" />
            </div>
          </AdvancedMarker>
        ))}
    </>
  );
};

const PopupMarker = ({
  entity,
  displaying,
  active,
}: {
  entity: MarkerEntityType;
  displaying: "buyer" | "supplier";
  active: boolean;
}) => {
  return (
    <>
      {active && displaying !== "buyer" ? (
        <SupplierInfoWindow entity={entity} />
      ) : (
        <div className="relative flex items-center gap-2 rounded-full border border-zinc-300 bg-white p-2 pr-3 text-sm font-medium text-nowrap text-zinc-800 shadow-md">
          <ProfilePicture
            logoUrl={entity.Business.logoUrl}
            name={entity.Business.name}
            size={"small"}
          />
          {entity.Business.name}
          <span className="absolute bottom-[-7px] left-[50%] ml-[-5px] h-3 w-3 rotate-45 border-r border-b border-zinc-300 bg-white" />
        </div>
      )}
    </>
  );
};

const SupplierInfoWindow = ({ entity }: { entity: MarkerEntityType }) => {
  const { track } = useEvents();
  const navigateToChild = useNavigateToChild();
  const supplierStorefrontQuery = useSupplierStorefrontV2(entity.id, {});
  const supplierStorefront = supplierStorefrontQuery.data;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const supplierQuery = useSupplier(entity.id);
  const supplier = supplierQuery.data;

  const [{ order }] = useBasketContext();

  const {
    apiValues: { price: orderTotal },
    calculatedContainerTypeDeliveryOffers,
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules:
      supplierStorefront?.FulfillmentType.DeliveryChargeRules,
    deliveryCharge: supplierStorefront?.FulfillmentType.charge,
    fulfillmentTypeContainerDeliveryCharges:
      supplierStorefront?.FulfillmentType
        .FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules:
      supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ??
      [],
    freeDeliveryBasketSubtotal,
  });
  const { sessionId } = useMarketStorefrontSessionId();

  const goToSupplier = () => {
    track("Discover supplier clicked", {
      supplier: entity.urlId,
      source: "Map pin",
      sessionId,
      ...getSellarWebsiteUtms(),
    });
    navigateToChild(
      ORDER_BUYER.href({ supplierIdOrUrlId: entity.urlId ?? "undefined" }),
    );
  };

  const { setActiveId } = useSellarMapStore(
    useShallow((state) => ({
      setActiveId: state.setActiveId,
    })),
  );

  const storefrontFulfillmentType =
    supplierStorefront?.FulfillmentType.fulfillmentType;

  const selectedFulfillmentType = getFulfillmentType(storefrontFulfillmentType);
  const fulfillmentType =
    selectedFulfillmentType === "collection"
      ? "direct"
      : selectedFulfillmentType;

  const unbundlingEnabled =
    supplierStorefront?.deliveryMethods &&
    supplierStorefront.deliveryMethods.length > 0;

  return (
    <InfoWindow
      position={entity.Business.Location}
      minWidth={300}
      maxWidth={350}
      className="text-base font-normal"
      headerContent={
        <>
          <div className="flex items-center gap-2 text-zinc-800">
            <ProfilePicture
              logoUrl={entity.Business.logoUrl}
              name={entity.Business.name}
              size={"large"}
              fallback="supplier-badge"
              className="flex-none"
            />
            <div className="flex flex-col gap-1">
              <span className="text-base font-semibold">
                {entity.Business.name}
              </span>

              {!!supplier && (
                <div className="flex items-center gap-1">
                  <ProfilePictureCollection className="flex flex-row">
                    {supplier.supplierProfileDetails.map((user, index) => (
                      <ProfilePictureCollectionImage
                        key={index}
                        imageUrl={user.profileImageUrl}
                        name={user.name}
                        size="small"
                      />
                    ))}
                  </ProfilePictureCollection>
                  <span className="text-xs font-normal">
                    {supplier.supplierRunByString}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setActiveId(null)}
            className="absolute top-0 right-0 z-10 rounded-lg bg-white p-2 hover:bg-zinc-50"
          >
            <Icon icon="CLOSE" />
          </button>
        </>
      }
    >
      <span className="my-2 block w-full border-t border-zinc-300" />
      {!!supplier && !!supplierStorefront ? (
        <>
          <div className="flex items-center gap-1 pt-1 pb-2 text-zinc-500">
            <Icon icon="MAP_MARKER" size={0.9} />
            <span className="text-sm font-semibold">
              {supplier.Business.DeliveryAddress.city},{" "}
              {supplier.Business.DeliveryAddress.postalCode}
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto">
            <div className={"flex-none border"}>
              <Card
                className={twMerge(
                  "bg-white",
                  "flex flex-none flex-col gap-2 border p-4",
                )}
              >
                <Badge icon={"TRUCK"}>Delivery options</Badge>
                {unbundlingEnabled ? (
                  <SelectedFulfillmentTypeCard
                    supplier={supplier}
                    selectedFulfillmentType={selectedFulfillmentType}
                    supplierStorefront={supplierStorefront}
                  />
                ) : (
                  <SelectedFulfillmentTypeCardLegacy
                    supplier={supplier}
                    selectedFulfillmentType={selectedFulfillmentType}
                    supplierStorefront={supplierStorefront}
                    fulfillmentType={{
                      ...supplierStorefront.FulfillmentType,
                      fulfillmentType,
                    }}
                    connection={supplierStorefront.SupplierRetailerConnection}
                  />
                )}
              </Card>
            </div>
            {priceTierDiscounts.length ? (
              <Card
                className={twMerge(
                  "bg-white",
                  "flex flex-none flex-col gap-2 border p-4",
                )}
              >
                <BadgePurple icon={"TAG_MULTIPLE_OUTLINE"}>
                  Volume discounts
                </BadgePurple>
                <OrderDiscountSummary
                  discounts={priceTierDiscounts}
                  currency={supplier.currency}
                  hideInfo={true}
                  caseSizeRuleList={supplierStorefront.CaseSizeRules.list}
                />
              </Card>
            ) : null}
            <StorefrontDeliveryOffersCard
              className={"flex-none rounded-md xl:border"}
              supplierStorefront={supplierStorefront}
              calculatedDeliveryOffers={calculatedContainerTypeDeliveryOffers}
              order={order}
            />
            <StorefrontOrderRulesCard
              className="flex-none rounded-md border"
              supplierStorefront={supplierStorefrontQuery.data}
              order={order}
              orderTotal={orderTotal}
            />
          </div>
        </>
      ) : (
        <SpinnerLegacy />
      )}
      <span className="my-2 block w-full border-t border-zinc-300" />
      <Button onClick={goToSupplier} className="w-full" size="small">
        Visit store <Icon icon="CHEVRON_RIGHT" />
      </Button>
    </InfoWindow>
  );
};
